import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { FaPrint } from 'react-icons/fa6';
import { fetchJSON, postJSON } from '../lib/api';
import { ReactComponent as Logo } from '../assets/logo-light.svg';
import CoursePlayerHeader from '../components/CoursePlayerHeader';
import CertificateSurvey from '../components/CertificateSurvey';
import { shouldShowSurvey } from '../lib/surveyStorage';

const SKILLS = [
  'AI Literacy',
  'Critical Thinking',
  'Risk Awareness',
  'Bias & Misinformation',
  'Responsible AI Use',
  'Practical AI Use',
];

const DISPLAY_COURSE_TITLE = 'Generative AI for Parents';

function todayLong() {
  return new Date().toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

const PRINT_CSS = `
  @media print {
    @page { size: letter landscape; margin: 0; }
    html, body {
      margin: 0 !important;
      padding: 0 !important;
      width: 11in !important;
      height: 8.5in !important;
      overflow: hidden !important;
      background: #ffffff !important;
    }
    .container-page {
      width: 11in !important;
      height: 8.5in !important;
      max-width: 11in !important;
      padding: 0.4in !important;
      box-sizing: border-box !important;
      display: flex !important;
      flex-direction: column !important;
      align-items: center !important;
      justify-content: center !important;
      overflow: hidden !important;
      page-break-after: avoid !important;
      break-after: avoid !important;
    }
    .container-page > div {
      width: 100% !important;
    }
    .certificate-card {
      max-width: 100% !important;
      width: 100% !important;
      margin: 0 !important;
      border-radius: 0 !important;
      box-shadow: none !important;
      page-break-inside: avoid;
      break-inside: avoid;
      zoom: 0.82;
    }
  }
`;

export default function CourseCertificate() {
  const { slug } = useParams();
  const { user, loading } = useAuth();
  const [course, setCourse] = useState(null);
  const [entitled, setEntitled] = useState(null);
  const [error, setError] = useState(null);

  // Name comes exclusively from the authenticated user's database record —
  // populated from Stripe billing details at purchase time (or from a gift/pilot
  // enrollment). No manual editing is allowed on this page.
  const name = (user && user.name) ? user.name.trim() : '';
  const dateStr = todayLong();

  const [showSurvey, setShowSurvey] = useState(() => shouldShowSurvey(slug));
  const [verifyCode, setVerifyCode] = useState(null);
  const isPilot = true;

  useEffect(() => {
    const el = document.createElement('style');
    el.setAttribute('data-cert-print', '1');
    el.textContent = PRINT_CSS;
    document.head.appendChild(el);
    return () => document.head.removeChild(el);
  }, []);

  useEffect(() => {
    if (user) {
      postJSON(`/api/courses/${slug}/certificate-view`, {})
        .then((data) => {
          if (data.verifyCode) setVerifyCode(data.verifyCode);
          // Survey only shows on the very first certificate view (server-confirmed).
          // This works across browsers/devices — localStorage alone cannot do this.
          if (!data.firstView) setShowSurvey(false);
        })
        .catch(() => {});
    }
  }, [slug, user]);

  useEffect(() => {
    let cancelled = false;
    setCourse(null);
    setEntitled(null);
    setError(null);
    fetchJSON(`/api/courses/${slug}`)
      .then((data) => {
        if (cancelled) return;
        const c = data.course || null;
        setCourse(c);
        const completeModule = c?.modules?.find((m) => m.slug === 'course-complete');
        const completeLesson = completeModule?.lessons?.[0];
        const hasLessonBody = Boolean(
          completeLesson && completeLesson.body_md && String(completeLesson.body_md).trim()
        );
        setEntitled(Boolean(data.reviewMode) || hasLessonBody);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.message);
        setEntitled(false);
      });
    return () => { cancelled = true; };
  }, [slug]);

  // Drive the document title from the learner's name so "Print to PDF" / "Save
  // as…" defaults the filename to something meaningful. Sanitized for the title
  // only — the visible certificate always shows the original name.
  useEffect(() => {
    const previousTitle = document.title;
    const sanitize = (s) =>
      s.replace(/[./\\:*?"<>|]+/g, '').replace(/\s+/g, ' ').trim();
    const cleaned = sanitize(name);
    const suffix = cleaned || `${DISPLAY_COURSE_TITLE} Certificate`;
    document.title = `Learning Outcomes Academy _ ${suffix}`;
    return () => { document.title = previousTitle; };
  }, [name]);

  // Wait for auth to resolve before deciding what to show
  if (loading) return null;

  // Redirect logged-out users to sign in
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (entitled === false && !error) {
    return <Navigate to={`/courses/${slug}`} replace />;
  }

  const displayName = name || 'Your Name';

  return (
    <div className="w-full bg-bone min-h-screen print:min-h-0">
      <div className="print:hidden">
        <CoursePlayerHeader
          courseTitle={course?.title || 'Certificate of Completion'}
          courseHref={`/courses/${slug}/course-complete/thank-you`}
        />
      </div>

      <div className="container-page py-12 lg:py-16 print:py-0">
        {/* Print button — hidden during survey and hidden when printing */}
        {!showSurvey && (
          <div className="max-w-3xl mx-auto mb-8 flex justify-end print:hidden">
            <button
              type="button"
              onClick={() => window.print()}
              className="btn-primary inline-flex items-center gap-2"
            >
              <FaPrint className="text-xs" /> Print or save as PDF
            </button>
          </div>
        )}

        {showSurvey && entitled ? (
          <CertificateSurvey
            slug={slug}
            isPilot={isPilot}
            onDone={() => setShowSurvey(false)}
          />
        ) : null}

        <div className={showSurvey ? 'hidden' : ''}>
          {error ? (
            <div className="max-w-3xl mx-auto p-6 rounded-2xl bg-red-50 text-red-800 text-sm print:hidden">
              {error}
            </div>
          ) : null}

          {/* CERTIFICATE */}
          <div
            id="certificate"
            className="certificate-card mx-auto max-w-3xl bg-bone border-[6px] border-double border-brand-deep rounded-2xl px-10 sm:px-16 py-10 sm:py-14 print:py-5 text-center shadow-[0_30px_60px_-30px_rgba(15,79,77,0.35)] print:shadow-none print:rounded-none print:border-[6px] print:max-w-full"
          >
            {/* Logo */}
            <div className="flex items-center justify-center">
              <Logo
                role="img"
                aria-label="Learning Outcomes Academy"
                className="h-16 sm:h-20 print:h-14 w-auto text-[#919091]"
              />
            </div>

            <div className="mt-8 text-[11px] tracking-[0.28em] uppercase text-slate2">
              Certificate of Completion
            </div>

            {/* Recipient name — the focal point of the certificate */}
            <div className="mt-10 mx-auto max-w-xl">
              <div className="font-display text-3xl sm:text-4xl text-brand-deep leading-tight px-6 pb-3 border-b border-ink/20">
                {displayName}
              </div>
            </div>

            <p className="mt-8 text-lg sm:text-xl text-slate2 leading-relaxed max-w-xl mx-auto">
              has successfully completed the course
            </p>

            <h2 className="mt-3 font-display text-2xl sm:text-3xl text-ink leading-tight">
              {DISPLAY_COURSE_TITLE}
            </h2>

            {/* Skills demonstrated */}
            <div className="mt-10 print:mt-6">
              <div className="flex items-center justify-center gap-3 mb-4">
                <span className="h-px w-8 bg-ink/20" />
                <span className="text-[10px] tracking-[0.16em] uppercase text-brand-aubergine">
                  Skills Developed &amp; Demonstrated
                </span>
                <span className="h-px w-8 bg-ink/20" />
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 print:grid-cols-3 gap-x-3 gap-y-3 items-stretch max-w-2xl mx-auto">
                {SKILLS.map((skill) => (
                  <span
                    key={skill}
                    className="inline-flex w-full items-center justify-center px-3 py-1.5 rounded-full bg-cream border border-brand-deep/20 text-xs font-display text-brand-deep text-center"
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </div>

            {/* Footer: Issued On (left) + Signature (right) */}
            <div className="mt-12 print:mt-5 grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8 max-w-2xl mx-auto items-end text-left">
              <div>
                <div className="text-[10px] tracking-[0.18em] uppercase text-brand-aubergine">
                  Issued On
                </div>
                <div className="mt-1 font-display text-base text-ink">
                  {dateStr}
                </div>
                <div className="mt-1 text-sm text-slate2">
                  Oshawa, ON, Canada
                </div>
              </div>

              <div className="text-left sm:text-right">
                <div className="inline-block text-left">
                  <img
                    src="/signature-qusay.png"
                    alt="Signature: Dr. Qusay H. Mahmoud"
                    className="h-14 sm:h-16 print:h-14 w-auto select-none"
                    draggable={false}
                  />
                  <div className="border-t border-ink/30 pt-1.5 mt-1">
                    <div className="font-display text-sm text-ink">
                      Dr. Qusay H. Mahmoud
                    </div>
                    <div className="text-[10px] tracking-[0.18em] uppercase text-brand-aubergine mt-0.5">
                      Course Facilitator
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-10 print:mt-4 flex items-center justify-center gap-3 text-brand-moss">
              <span className="h-px w-10 bg-brand-moss/40" />
              <span className="text-[10px] tracking-[0.32em] uppercase text-brand-moss">
                Learning · Conversation · Growth
              </span>
              <span className="h-px w-10 bg-brand-moss/40" />
            </div>

            {verifyCode && (
              <div className="mt-3 print:mt-2 text-center">
                <span className="text-[10px] text-slate2 tracking-wide">
                  Verify at{' '}
                  <span className="font-mono">
                    learningoutcomes.academy/verify/{verifyCode}
                  </span>
                </span>
              </div>
            )}
          </div>

          <div className="max-w-3xl mx-auto mt-10 text-center print:hidden">
            <Link
              to={`/courses/${slug}/course-complete/thank-you`}
              className="btn-link inline-flex"
            >
              Back to Course Complete
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
}
